import React, { useEffect, useMemo, useState } from 'react';
import { fetchMaterialById } from '../api/materials';

const DEFAULT_THRESHOLDS = {
  pm1: 1,
  pm25: 25,
  pm10: 50,
};

const UI_COLOR_OPTIONS = [
  'Czarny',
  'Przezroczysty',
  'Biały',
  'Szary',
  'Srebrny',
  'Złoty',
  'Czerwony',
  'Pomarańczowy',
  'Żółty',
  'Zielony',
  'Niebieski',
  'Granatowy',
  'Fioletowy',
  'Różowy',
  'Brązowy',
  'Beżowy',
];

function bucketOfColor(value) {
  const s = String(value || '').trim().toLowerCase();
  if (!s) return null;
  if (s.includes('czarn') || s === 'black') return 'black';
  if (s.includes('przezro') || s.includes('transparent') || s.includes('clear')) return 'transparent';
  return 'other';
}

function isOtherBucketSelection(value) {
  const b = bucketOfColor(value);
  return b === 'other';
}

export default function Pollution({ local = {}, meta = {} }) {
  const [contaminants, setContaminants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedColor, setSelectedColor] = useState('');
  const [productMass, setProductMass] = useState('');
  const [result, setResult] = useState(null);
  const [effectiveColor, setEffectiveColor] = useState(null);

  const thresholds = meta?.thresholds ?? DEFAULT_THRESHOLDS;

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!local?.id) return;
      setLoading(true);
      setError(null);
      try {
        const mat = await fetchMaterialById(local.id);
        if (!mounted) return;
        setContaminants(Array.isArray(mat?.contaminants) ? mat.contaminants : []);
      } catch (e) {
        if (mounted) setError(e.message || 'Błąd pobierania danych');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => (mounted = false);
  }, [local?.id]);

  const colors = useMemo(() => {
    const set = new Set(UI_COLOR_OPTIONS);
    contaminants.forEach((c) => {
      if (c.filament_color) set.add(String(c.filament_color));
    });
    return Array.from(set).sort();
  }, [contaminants]);

  const otherSourceColor = useMemo(() => {
    const counts = new Map();
    contaminants.forEach((c) => {
      const col = c?.filament_color;
      if (!col) return;
      if (bucketOfColor(col) !== 'other') return;
      const key = String(col);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    let best = null;
    let bestCount = -1;
    for (const [k, v] of counts.entries()) {
      if (v > bestCount) {
        best = k;
        bestCount = v;
      }
    }
    return best;
  }, [contaminants]);

  const massValue = useMemo(() => Number(productMass), [productMass]);

  const isCalculateDisabled = !selectedColor || !Number.isFinite(massValue) || massValue <= 0;

  function calculate() {
    if (isCalculateDisabled) {
      setResult(null);
      setEffectiveColor(null);
      return;
    }

    const selectedBucket = bucketOfColor(selectedColor);

    let effective = selectedColor;
    if (selectedBucket === 'other') {
      const hasExact = contaminants.some((c) =>
        String(c?.filament_color) === String(selectedColor) && Number(c?.product_mass) > 0
      );
      effective = hasExact ? selectedColor : (otherSourceColor || selectedColor);
    }
    setEffectiveColor(effective);

    const rows = contaminants.filter(
      (c) =>
        bucketOfColor(c.filament_color) === selectedBucket &&
        (selectedBucket !== 'other' || String(c.filament_color) === String(effective)) &&
        Number(c.product_mass) > 0
    );

    if (!rows.length) {
      setResult({ pm1: null, pm25: null, pm10: null, _noData: true });
      return;
    }

    const sums = rows.reduce(
      (acc, r) => {
        const rowMass = Number(r.product_mass) || 0;
        acc.sumMass += rowMass;
        acc.sumPm1 += Number(r.pm1) || 0;
        acc.sumPm25 += Number(r.pm25) || 0;
        acc.sumPm10 += Number(r.pm10) || 0;
        return acc;
      },
      { sumMass: 0, sumPm1: 0, sumPm25: 0, sumPm10: 0 }
    );

    if (sums.sumMass <= 0) {
      setResult(null);
      return;
    }

    const factor = massValue / sums.sumMass;

    setResult({
      pm1: null,
      pm25: +(sums.sumPm25 * factor).toFixed(3),
      pm10: +(sums.sumPm10 * factor).toFixed(3),
      _noData: false,
    });
  }

  const hasViolation = useMemo(() => {
    if (!result) return false;
    if (result._noData) return false;
    return (
      result.pm25 > thresholds.pm25 ||
      result.pm10 > thresholds.pm10
    );
  }, [result, thresholds]);

  function handleColorChange(e) {
    setSelectedColor(e.target.value);
    setResult(null);
    setEffectiveColor(null);
  }

  function handleMassChange(e) {
    setProductMass(e.target.value);
    setResult(null);
    setEffectiveColor(null);
  }

  return (
    <div>
      <strong>Zanieczyszczenia</strong>

      {loading && <div className="small text-muted">Ładowanie danych…</div>}
      {error && <div className="small text-danger">{error}</div>}

      <div className="card mt-2">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-5">
              <label className="form-label small">Kolor filamentu</label>
              <select
                className="form-select form-select-sm"
                value={selectedColor}
                onChange={handleColorChange}
              >
                <option value="">— wybierz kolor —</option>
                {colors.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              {/*{isOtherBucketSelection(selectedColor) && (
                <div className="form-text">
                  {effectiveColor && String(effectiveColor) !== String(selectedColor)
                    ? <>Brak danych dla: <strong>{selectedColor}</strong>. Użyto danych dla: <strong>{effectiveColor}</strong>.</>
                    : <>Dla tego koloru używane są dane dla wybranego koloru (jeśli dostępne), w przeciwnym razie z innego dostępnego koloru.</>
                  }
                </div>
              )}*/}
            </div>

            <div className="col-md-4">
              <label className="form-label small">Masa wyrobu (g)</label>
              <input
                type="number"
                className="form-control form-control-sm"
                min="0"
                step="0.1"
                value={productMass}
                onChange={handleMassChange}
              />
            </div>

            <div className="col-md-3">
              <button
                className="btn btn-sm btn-primary w-100"
                disabled={isCalculateDisabled}
                onClick={calculate}
              >
                Oblicz
              </button>
            </div>
          </div>
        </div>
      </div>

      {result && (
        <div className="mt-3">
          {hasViolation && (
            <div className="alert alert-danger">
              <strong>Uwaga:</strong> Przekroczone dopuszczalne stężenia.
              <div className="mt-1">
                Zalecane: intensywne wietrzenie pomieszczenia i stosowanie ochrony dróg oddechowych.
              </div>
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>PM1 [µg/m³]</th>
                  <th>PM2.5 [µg/m³]</th>
                  <th>PM10 [µg/m³]</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  {/* Funkcja PM1 czasowo zablokowana */}
                  {/*<td className={result.pm1 > thresholds.pm1 ? 'text-danger fw-bold' : ''}>
                    {result.pm1}
                  </td>*/}
                  <td>Niedostępne</td>
                  <td className={result.pm25 > thresholds.pm25 ? 'text-danger fw-bold' : ''}>
                    {result._noData ? 'Brak danych' : result.pm25}
                  </td>
                  <td className={result.pm10 > thresholds.pm10 ? 'text-danger fw-bold' : ''}>
                    {result._noData ? 'Brak danych' : result.pm10}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
            <div className="alert alert-info">
              <div className="mt-1">
                Wyniki kalkulatora zanieczyszczeń są oparte o badania naukowe.
              </div>
            </div>
        </div>
      )}
    </div>
  );
}